import React, { useState } from 'react';
import { ClientEntity } from '../types';
import { useStore } from '../store/useStore';
import { 
  Calendar as CalendarIcon, 
  Users, 
  Settings, 
  CheckCircle2, 
  MapPin, 
  Video, 
  Tag, 
  User, 
  Clock,
  Filter,
  ArrowRight,
  Zap,
  Phone,
  BarChart2,
  List,
  Network,
  BellRing,
  Link as LinkIcon,
  Plus,
  DollarSign,
  CalendarCheck,
  AlertCircle,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Trash2,
  X,
  Copy,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const generateUniqueId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `apt_${Math.random().toString(36).substring(2, 9)}`;
};

export const SchedulingDashboard: React.FC<{ client: ClientEntity }> = ({ client }) => {
  const { appointments, setAppointments, addAppointment, updateAppointment, responsibles, products } = useStore();
  const [activeTab, setActiveTab] = useState<'visao' | 'calendario' | 'servicos' | 'equipe' | 'lembretes'>('visao');
  const [isNewAppointmentModalOpen, setIsNewAppointmentModalOpen] = useState(false);

  // New appointment form state
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [aptDate, setAptDate] = useState(new Date().toISOString().split('T')[0]);
  const [aptTime, setAptTime] = useState('14:00');
  const [assignedRep, setAssignedRep] = useState('');

  // Rules state
  const [rules, setRules] = useState([
    { id: 1, ifField: 'Serviço Solicitado', cond: 'Qualquer um', assignTo: 'Roleta Automática' }
  ]);
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);
  const [newRuleField, setNewRuleField] = useState('Serviço Solicitado');
  const [newRuleCond, setNewRuleCond] = useState('');
  const [newRuleRep, setNewRuleRep] = useState('');

  // Reminders state
  const [reminderImmediate, setReminderImmediate] = useState("Fala {{nome_lead}}, confirmado! Já bloqueamos a agenda. O/A {{nome_vendedor}} estará te aguardando! 🚀");
  const [reminder24h, setReminder24h] = useState("Oi {{nome_lead}}, amanhã você tem compromisso marcado com o(a) {{nome_vendedor}} sobre {{servico}}. Podemos confirmar que continua de pé?");
  const [reminder2h, setReminder2h] = useState("Atenção {{nome_lead}}! É em menos de 2 horas. Segue aqui o link/endereço novamente. O(a) {{nome_vendedor}} está te aguardando. Até já!");

  const activeReps = (responsibles || []).filter(r => r.nome?.trim() !== '');
  const activeServices = (products || []).filter(p => p.nome?.trim() !== '');

  // Dynamic metrics from appointments
  const totalCount = appointments?.length || 0;
  const confirmedCount = (appointments || []).filter(a => a.status === 'confirmado' || a.status === 'agendado').length;
  const showRate = totalCount > 0 ? Math.round((confirmedCount / totalCount) * 100) : 0;
  const noShowCount = (appointments || []).filter(a => a.status === 'cancelado').length;

  const handleCreateAppointment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientName.trim()) {
      toast.error('Informe o nome do cliente');
      return;
    }

    const selectedServiceObj = activeServices.find(s => s.nome === serviceName) || activeServices[0];
    const newApt = {
      id: generateUniqueId(),
      clientId: client.id,
      clientName: clientName.trim(),
      clientPhone: clientPhone.trim() || '11 99999-9999',
      serviceId: selectedServiceObj?.id || '1',
      serviceName: serviceName || selectedServiceObj?.nome || 'Consultoria Geral',
      date: aptDate,
      time: aptTime,
      status: 'confirmado' as const,
      assignedTo: assignedRep || (activeReps[0]?.nome || 'Equipe Principal'),
      createdAt: new Date().toISOString()
    };

    addAppointment(newApt);
    toast.success(`Agendamento para ${newApt.clientName} registrado com sucesso!`);
    
    // Reset
    setClientName('');
    setClientPhone('');
    setIsNewAppointmentModalOpen(false);
  };

  const handleCopyBookingLink = (serviceName?: string) => {
    const portalUrl = `${window.location.origin}${window.location.pathname}#/atendimento/${client.slug}`;
    navigator.clipboard.writeText(portalUrl);
    toast.success(`Link de agendamento ${serviceName ? `para "${serviceName}"` : ''} copiado!`);
  };

  const handleSaveReminders = () => {
    toast.success('Régua Anti No-Show salva e sincronizada com o Agente WhatsApp!');
  };

  const handleAddRule = (e: React.FormEvent) => {
    e.preventDefault();
    setRules([...rules, {
      id: Date.now(),
      ifField: newRuleField,
      cond: newRuleCond || 'Todos',
      assignTo: newRuleRep || 'Roleta Automática'
    }]);
    toast.success('Regra de roteamento adicionada com sucesso!');
    setIsAddRuleModalOpen(false);
  };

  const handleDeleteRule = (ruleId: number) => {
    setRules(rules.filter(r => r.id !== ruleId));
    toast.success('Regra removida.');
  };

  const handleSyncGoogleCalendar = (repName: string) => {
    toast.success(`Sincronização do Google Calendar solicitada para ${repName}!`);
  };

  const times = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
  const days = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div className="h-full flex flex-col w-full max-w-7xl mx-auto pb-12 animate-in fade-in duration-300">
      
      {/* Top Header */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <span>Sistema de Agendamento & Calendário</span>
            <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full font-bold border border-emerald-200">
              Integrado ao WhatsApp
            </span>
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Grade de horários, serviços prestados, roteamento da equipe comercial e automação de lembretes.
          </p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <button 
            onClick={() => handleCopyBookingLink()}
            className="flex-1 md:flex-none bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
          >
            <LinkIcon size={15} />
            Link Público
          </button>

          <button 
            onClick={() => setIsNewAppointmentModalOpen(true)}
            className="flex-1 md:flex-none bg-black hover:bg-gray-800 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            Novo Agendamento
          </button>
        </div>
      </div>

      {/* Tabs Switcher */}
      <div className="flex bg-white p-1 rounded-xl w-fit mb-6 shadow-sm border border-gray-200 overflow-x-auto max-w-full hide-scrollbar">
        {[
          { id: 'visao', label: 'Dashboard & Métricas', icon: BarChart2 },
          { id: 'calendario', label: 'Calendário Master', icon: CalendarIcon },
          { id: 'servicos', label: 'Tipos de Serviços', icon: List },
          { id: 'equipe', label: 'Equipe & Roteamento', icon: Users },
          { id: 'lembretes', label: 'Régua Lembretes', icon: BellRing },
        ].map(item => (
          <button 
            key={item.id}
            onClick={() => setActiveTab(item.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === item.id ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <item.icon size={16} className={activeTab === item.id ? 'text-white' : 'text-gray-400'} />
            {item.label}
          </button>
        ))}
      </div>

      {/* Tab 1: Visao Geral & Métricas */}
      {activeTab === 'visao' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* Dynamic Status Alert */}
          {totalCount === 0 ? (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 flex items-start gap-4 shadow-sm">
              <div className="p-2 bg-orange-100 rounded-lg text-orange-600 shrink-0">
                <CalendarIcon size={24} />
              </div>
              <div className="flex-1">
                <h4 className="text-orange-950 font-bold text-sm">Agenda Pronta para Receber Clientes</h4>
                <p className="text-xs text-orange-800 mt-1">
                  Nenhum agendamento ativo nos próximos dias. Compartilhe o link do seu Portal do Cliente ou divulgue seus serviços pelo WhatsApp para começar a receber reservas.
                </p>
                <button 
                  onClick={() => setIsNewAppointmentModalOpen(true)}
                  className="mt-3 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors inline-flex items-center gap-1"
                >
                  <Plus size={13} /> Agendar Primeiro Horário
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-start gap-4 shadow-sm">
              <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600 shrink-0">
                <CalendarCheck size={24} />
              </div>
              <div>
                <h4 className="text-emerald-950 font-bold text-sm">Agenda Ativa e Sincronizada</h4>
                <p className="text-xs text-emerald-800 mt-1">
                  Você possui <strong>{totalCount} agendamento(s)</strong> registrado(s). As notificações automáticas do WhatsApp manterão seus clientes atualizados.
                </p>
              </div>
            </div>
          )}

          {/* Cards de Métricas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <CalendarIcon size={22} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 leading-none">{totalCount}</div>
                <p className="text-gray-900 text-sm font-semibold mt-1">Agendamentos</p>
                <p className="text-xs text-gray-400 mt-0.5">Total registrado</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <CheckCircle2 size={22} />
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-600 leading-none">{totalCount > 0 ? `${showRate}%` : '100%'}</div>
                <p className="text-gray-900 text-sm font-semibold mt-1">Show Rate</p>
                <p className="text-xs text-gray-400 mt-0.5">Taxa de presença</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                <AlertCircle size={22} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 leading-none">{noShowCount}</div>
                <p className="text-gray-900 text-sm font-semibold mt-1">No-Show</p>
                <p className="text-xs text-gray-400 mt-0.5">Ausências registradas</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <DollarSign size={22} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 leading-none">{confirmedCount}</div>
                <p className="text-gray-900 text-sm font-semibold mt-1">Confirmados</p>
                <p className="text-xs text-gray-400 mt-0.5">Sessões marcadas</p>
              </div>
            </div>
          </div>

          {/* List of Recent Appointments */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Últimos Agendamentos Recebidos</h3>
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {totalCount} registro(s)
              </span>
            </div>

            {totalCount > 0 ? (
              <div className="divide-y divide-gray-100">
                {appointments.map((apt) => (
                  <div key={apt.id} className="py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50/80 px-2 rounded-xl transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 font-bold text-sm">
                        {apt.clientName?.charAt(0) || 'C'}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">{apt.clientName}</h4>
                        <p className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                          <Tag size={12} className="text-gray-400" /> {apt.serviceName}
                          <span className="text-gray-300">•</span>
                          <User size={12} className="text-gray-400" /> {apt.assignedTo || 'Geral'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xs font-bold text-gray-900 flex items-center gap-1">
                          <Clock size={12} className="text-gray-400" /> {apt.date} às {apt.time}
                        </p>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                          {apt.status || 'Confirmado'}
                        </span>
                      </div>

                      <button
                        onClick={() => {
                          const clean = (apt.clientPhone || '').replace(/\D/g, '');
                          window.open(`https://wa.me/55${clean}?text=Olá ${apt.clientName}, confirmando seu agendamento no dia ${apt.date}!`, '_blank');
                        }}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Enviar WhatsApp"
                      >
                        <Phone size={15} />
                      </button>

                      <button
                        onClick={() => {
                          setAppointments(appointments.filter(a => a.id !== apt.id));
                          toast.success('Agendamento cancelado com sucesso.');
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remover"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                <CalendarIcon size={36} className="mx-auto mb-2 opacity-30 text-gray-500" />
                <p className="text-sm font-semibold text-gray-700">Nenhum compromisso marcado</p>
                <p className="text-xs text-gray-500 mt-0.5">Clique em "+ Novo Agendamento" para adicionar manualmente.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 2: Calendario Master */}
      {activeTab === 'calendario' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors">
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm font-bold text-gray-900">Semana Atual</span>
              <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors">
                <ChevronRight size={18} />
              </button>
            </div>

            <button 
              onClick={() => setIsNewAppointmentModalOpen(true)}
              className="bg-black hover:bg-gray-800 text-white text-xs font-bold px-4 py-2 rounded-xl transition-colors flex items-center gap-1.5"
            >
              <Plus size={14} /> Novo Horário
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 text-center text-xs font-bold text-gray-700 py-3 uppercase tracking-wider">
              <div className="border-r border-gray-200">Horário</div>
              {days.map(d => (
                <div key={d} className="border-r border-gray-200 last:border-r-0">{d}</div>
              ))}
            </div>

            <div className="divide-y divide-gray-100 max-h-[550px] overflow-y-auto">
              {times.map(t => (
                <div key={t} className="grid grid-cols-7 min-h-[60px]">
                  <div className="p-3 border-r border-gray-100 text-xs font-mono text-gray-400 text-center flex items-center justify-center bg-gray-50/30">
                    {t}
                  </div>
                  {days.map((d, dIdx) => {
                    const aptForSlot = appointments.find(a => a.time === t);
                    return (
                      <div 
                        key={`${d}-${t}`} 
                        onClick={() => {
                          if (!aptForSlot) {
                            setAptTime(t);
                            setIsNewAppointmentModalOpen(true);
                          }
                        }}
                        className="border-r border-gray-100 last:border-r-0 p-1.5 hover:bg-indigo-50/40 transition-colors cursor-pointer relative"
                      >
                        {aptForSlot && dIdx === 1 && (
                          <div className="bg-indigo-600 text-white p-1.5 rounded-lg text-[10px] shadow-sm font-semibold truncate">
                            <span className="block truncate">{aptForSlot.clientName}</span>
                            <span className="text-indigo-200 text-[9px] block truncate">{aptForSlot.serviceName}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Tipos de Servicos */}
      {activeTab === 'servicos' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Catálogo de Serviços Habilitados</h3>
            <p className="text-sm text-gray-500 mb-6">
              Serviços configurados para agendamento online e atendimento pelo robô no WhatsApp.
            </p>

            {activeServices.length > 0 ? (
              <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                {activeServices.map(svc => (
                  <div key={svc.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-gray-50">
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">{svc.nome}</h4>
                      <p className="text-xs text-gray-500 mt-0.5">{svc.descricao || 'Atendimento com horário marcado'}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs font-semibold">
                        <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                          Preço: {svc.preco || 'Sob Consulta'}
                        </span>
                        <span className="text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                          Duração: {svc.tempoEntrega || '45 min'}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => handleCopyBookingLink(svc.nome)}
                      className="bg-black hover:bg-gray-800 text-white text-xs font-bold px-4 py-2 rounded-xl transition-colors flex items-center justify-center gap-1.5 shadow-sm shrink-0"
                    >
                      <Copy size={13} /> Copiar Link do Serviço
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <List size={32} className="mx-auto mb-2 text-gray-300" />
                <h4 className="font-bold text-gray-900 text-sm">Nenhum serviço cadastrado</h4>
                <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                  Cadastre seus serviços no menu "Meu Negócio" para habilitá-los automaticamente aqui.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 4: Equipe & Roteamento */}
      {activeTab === 'equipe' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Regras de Distribuição (Routing)</h3>
                <p className="text-xs text-gray-500">Defina como novos agendamentos são distribuídos entre os membros da equipe.</p>
              </div>

              <button 
                onClick={() => setIsAddRuleModalOpen(true)}
                className="bg-black hover:bg-gray-800 text-white text-xs font-bold px-4 py-2 rounded-xl transition-colors flex items-center gap-1.5"
              >
                <Plus size={14} /> Adicionar Regra
              </button>
            </div>

            <div className="space-y-3">
              {rules.map(rule => (
                <div key={rule.id} className="flex flex-wrap items-center justify-between gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm">
                  <div className="flex items-center gap-2">
                    <span className="bg-white border border-gray-200 px-2.5 py-1 rounded-md text-xs font-bold text-gray-600">SE</span>
                    <span className="font-semibold text-gray-900 text-xs">{rule.ifField}</span>
                    <span className="text-gray-400 text-xs">=</span>
                    <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-md text-xs font-bold">{rule.cond}</span>
                    <span className="text-emerald-600 text-xs font-bold">→ Atribuir para:</span>
                    <span className="font-bold text-gray-900 text-xs">{rule.assignTo}</span>
                  </div>

                  <button 
                    onClick={() => handleDeleteRule(rule.id)}
                    className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Membros da Equipe & Google Calendar</h3>
            <div className="divide-y divide-gray-100">
              {activeReps.map(rep => (
                <div key={rep.id} className="py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{rep.nome}</h4>
                    <p className="text-xs text-gray-500">{rep.cargo || rep.area || 'Atendente'} • {rep.whatsapp || 'WhatsApp não informado'}</p>
                  </div>

                  <button
                    onClick={() => handleSyncGoogleCalendar(rep.nome)}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <CheckCircle2 size={13} /> Sincronizar Google Agenda
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab 5: Régua Lembretes */}
      {activeTab === 'lembretes' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="bg-emerald-900 text-white p-6 rounded-2xl shadow-md relative overflow-hidden">
            <h3 className="text-xl font-bold mb-1">Régua Anti No-Show Automática</h3>
            <p className="text-xs text-emerald-200 max-w-xl">
              Mensagens personalizadas disparadas automaticamente pelo robô no WhatsApp para garantir presença e evitar faltas.
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-600 mb-2 flex items-center gap-1.5">
                <CheckCircle2 size={14} /> Imediatamente após agendar
              </label>
              <textarea 
                value={reminderImmediate}
                onChange={(e) => setReminderImmediate(e.target.value)}
                rows={2}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-mono text-gray-800 outline-none focus:bg-white focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-emerald-600 mb-2 flex items-center gap-1.5">
                <CalendarCheck size={14} /> 24 Horas Antes da Reunião
              </label>
              <textarea 
                value={reminder24h}
                onChange={(e) => setReminder24h(e.target.value)}
                rows={2}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-mono text-gray-800 outline-none focus:bg-white focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1.5">
                <Clock size={14} /> 2 Horas Antes da Reunião
              </label>
              <textarea 
                value={reminder2h}
                onChange={(e) => setReminder2h(e.target.value)}
                rows={2}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-mono text-gray-800 outline-none focus:bg-white focus:ring-2 focus:ring-black"
              />
            </div>

            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={handleSaveReminders}
                className="bg-black hover:bg-gray-800 text-white font-bold text-sm px-6 py-2.5 rounded-xl transition-all shadow-sm"
              >
                Salvar Régua de Lembretes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Novo Agendamento Manual */}
      {isNewAppointmentModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-lg">Novo Agendamento</h3>
              <button 
                onClick={() => setIsNewAppointmentModalOpen(false)}
                className="text-gray-400 hover:text-gray-900 p-1.5 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateAppointment} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Nome do Paciente / Cliente
                </label>
                <input 
                  type="text"
                  required
                  placeholder="Ex: Fernanda Lima"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  WhatsApp do Cliente
                </label>
                <input 
                  type="text"
                  placeholder="Ex: 11 98888-7777"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Serviço Desejado
                </label>
                <select
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                >
                  <option value="">Selecione um serviço</option>
                  {activeServices.map(s => (
                    <option key={s.id} value={s.nome}>{s.nome} ({s.preco || 'Consultar'})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                    Data
                  </label>
                  <input 
                    type="date"
                    required
                    value={aptDate}
                    onChange={(e) => setAptDate(e.target.value)}
                    className="w-full px-3.5 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                    Horário
                  </label>
                  <select
                    value={aptTime}
                    onChange={(e) => setAptTime(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                  >
                    {times.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Profissional Responsável
                </label>
                <select
                  value={assignedRep}
                  onChange={(e) => setAssignedRep(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                >
                  <option value="">Equipe Geral</option>
                  {activeReps.map(r => (
                    <option key={r.id} value={r.nome}>{r.nome}</option>
                  ))}
                </select>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsNewAppointmentModalOpen(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-black hover:bg-gray-800 text-white px-5 py-2.5 text-sm font-bold rounded-xl shadow-sm"
                >
                  Confirmar Horário
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Adicionar Regra */}
      {isAddRuleModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-lg">Nova Regra de Roteamento</h3>
              <button 
                onClick={() => setIsAddRuleModalOpen(false)}
                className="text-gray-400 hover:text-gray-900 p-1.5 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAddRule} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Campo da Condição
                </label>
                <select
                  value={newRuleField}
                  onChange={(e) => setNewRuleField(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none"
                >
                  <option value="Serviço Solicitado">Serviço Solicitado</option>
                  <option value="Cidade / Região">Cidade / Região</option>
                  <option value="Mensagem do Lead">Mensagem Contém Palavra-chave</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Valor / Condição
                </label>
                <input 
                  type="text"
                  placeholder="Ex: Consulta Premium"
                  value={newRuleCond}
                  onChange={(e) => setNewRuleCond(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Direcionar para
                </label>
                <select
                  value={newRuleRep}
                  onChange={(e) => setNewRuleRep(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none"
                >
                  <option value="Roleta Automática">Roleta Automática (Equipe)</option>
                  {activeReps.map(r => (
                    <option key={r.id} value={r.nome}>{r.nome}</option>
                  ))}
                </select>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsAddRuleModalOpen(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-black hover:bg-gray-800 text-white px-5 py-2.5 text-sm font-bold rounded-xl"
                >
                  Salvar Regra
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
